import { CommunityId, emailTokens } from "@sp-crm/core";
import { MandatoryEmailToken } from "components/messages/mandatory-token";
import { MessageComposeInline } from "components/messages/message-compose-internal";
import { PlacementMessageCompose } from "components/messages/types";
import { fancyAlert } from "components/ui/fancy-alert";
import { InlineBanner } from "components/ui/inline-banner";
import { PanelType } from "components/ui/panel/panel-type";
import { ResponsivePanel } from "components/ui/panel/responsive-panel";
import { PrimaryButton } from "components/ui/primary-button";
import { SecondaryButton } from "components/ui/secondary-button";
import {
    AdvancedSearchEntityType,
    useRequestCommunityListingParticipationMutation,
} from "generated/graphql";
import React, { MutableRefObject, useCallback } from "react";
import { useCanRequestCommunityAvailability } from "store/selectors/hooks";

interface Props {
    communityId: CommunityId;
    onDismiss: () => void;
    onSuccess: () => void;
}

export const CommunityRequestAvailabilityPanel: React.FC<Props> = props => {
    const canRequestAvailability = useCanRequestCommunityAvailability();
    const { communityId, onDismiss, onSuccess } = props;
    const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

    const inlineEditorRef =
        React.useRef<PlacementMessageCompose>() as MutableRefObject<PlacementMessageCompose>;
    const requestCommunityListingParticipation =
        useRequestCommunityListingParticipationMutation();

    const handleSend = useCallback(async () => {
        const message = inlineEditorRef.current.getMessage();
        try {
            if (!(message.body || "").includes(emailTokens.liveLink)) {
                const errorMessage = `To send the form the message body must contain ${emailTokens.liveLink}. To proceed, add ${emailTokens.liveLink} to the body and send again.`;
                setErrorMessage(errorMessage);
                await fancyAlert("Link Placeholder Missing", errorMessage);
                return;
            }
            setErrorMessage(null);

            await requestCommunityListingParticipation.mutateAsync({
                communityId,
                outboundMessage: message,
            });
            onSuccess();
        } catch (e) {
            setErrorMessage(e.message);
        }
    }, [communityId, inlineEditorRef, requestCommunityListingParticipation, onSuccess]);

    if (!canRequestAvailability) {
        return null;
    }

    return (
        <ResponsivePanel
            type={PanelType.extraLarge}
            onDismiss={onDismiss}
            isOpen={true}
            headerText="Request community availability">
            <div className="space-y-4">
                <p>
                    Sends a message with a link the community can use to share their
                    availability.
                </p>
                <MessageComposeInline
                    entityId={communityId}
                    entityType={AdvancedSearchEntityType.Community}
                    onDismiss={onDismiss}
                    ref={inlineEditorRef}
                    requestSpecialMessageExperience="request-availability"
                    initialBody="Hi!<br><br>When you have a moment, could you update your availability below? It's really quick, and will help me know as soon as you have vacancies. Thanks so much!<br><br>{{link}}"
                    initialSubject="Any openings right now?"
                />
                <div className="my-1">
                    <MandatoryEmailToken use="request-availability" />
                </div>
                <div className="flex items-center justify-end space-x-2">
                    <SecondaryButton type="button" onClick={onDismiss}>
                        Discard
                    </SecondaryButton>
                    <PrimaryButton type="button" onClick={handleSend}>
                        Send
                    </PrimaryButton>
                </div>
                {errorMessage ? (
                    <InlineBanner type="error">{errorMessage}</InlineBanner>
                ) : null}
            </div>
        </ResponsivePanel>
    );
};
